import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { enablePush, disablePush, pushSupported } from "@/lib/push";
import { Bell, Mail, Send } from "lucide-react";

const Toggle = ({ on, onChange, testid, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    disabled={disabled}
    onClick={() => onChange(!on)}
    data-testid={testid}
    className="relative w-12 h-6 shrink-0 transition-colors disabled:opacity-50"
    style={{ background: on ? "#FF0055" : "#27272A", border: "1px solid #3f3f46" }}
  >
    <span className="absolute top-0.5 w-4 h-4 bg-white transition-transform" style={{ left: 2, transform: on ? "translateX(24px)" : "translateX(0)" }} />
  </button>
);

export const ReminderSettings = () => {
  const [prefs, setPrefs] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/reminders").then((r) => setPrefs(r.data)).catch(() => {});
  }, []);

  if (!prefs) return null;

  const setEmail = async (v) => {
    const { data } = await api.patch("/reminders", { email_enabled: v });
    setPrefs(data);
  };

  const setPush = async (v) => {
    setBusy(true);
    setMsg("");
    try {
      if (v) await enablePush(); else await disablePush();
      const { data } = await api.patch("/reminders", { push_enabled: v });
      setPrefs(data);
    } catch (e) {
      setMsg(e.message);
    }
    setBusy(false);
  };

  const sendNow = async () => {
    setBusy(true);
    setMsg("");
    const { data } = await api.post("/reminders/send-now");
    const parts = [];
    if (data.email) parts.push(data.email.ok ? "Email sent." : `Email: ${data.email.error}`);
    if (data.push) parts.push(data.push.sent ? `Push sent to ${data.push.sent} device(s).` : `Push: ${data.push.errors?.[0] || "no devices"}`);
    setMsg(parts.join(" ") || "Turn on at least one channel first.");
    setBusy(false);
  };

  return (
    <section className="mt-12" data-testid="reminder-settings">
      <p className="eyebrow">Sunday reminder</p>
      <h2 className="font-display font-black text-2xl md:text-3xl mt-1">NEVER SKIP <span style={{ color: "#FF0055" }}>THE CHECK-IN</span></h2>
      <p className="text-sm text-zinc-400 mt-2">{prefs.schedule} · {prefs.timezone}</p>
      <p className="text-xs text-zinc-500 mt-1" data-testid="reminder-sender">
        From: <span className="text-zinc-300">{prefs.sender_email}</span>
        {prefs.email_test_mode && <span style={{ color: "#FF0055" }}> · Test mode — emails only reach the Resend account owner until a domain is verified.</span>}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6">
        <div className="card flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Mail size={18} color="#FF0055" />
            <div>
              <p className="font-display font-bold uppercase text-sm">Email nudge</p>
              <p className="text-xs text-zinc-500 mt-0.5">Sunday morning in your inbox</p>
            </div>
          </div>
          <Toggle on={prefs.email_enabled} onChange={setEmail} testid="reminder-email-toggle" />
        </div>
        <div className="card flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Bell size={18} color="#FF0055" />
            <div>
              <p className="font-display font-bold uppercase text-sm">Push notification</p>
              <p className="text-xs text-zinc-500 mt-0.5">
                {pushSupported() ? `${prefs.push_subscriptions} device(s) on this account` : "Not supported in this browser"}
              </p>
            </div>
          </div>
          <Toggle on={prefs.push_enabled} onChange={setPush} testid="reminder-push-toggle" disabled={busy || !pushSupported()} />
        </div>
      </div>

      <div className="mt-4 flex items-center gap-4 flex-wrap">
        <button className="btn-secondary flex items-center gap-2" onClick={sendNow} disabled={busy} data-testid="reminder-send-now">
          <Send size={14} /> {busy ? "Sending…" : "Send me a test nudge"}
        </button>
        {msg && <p className="text-xs text-zinc-400 max-w-md" data-testid="reminder-status">{msg}</p>}
      </div>
    </section>
  );
};
